import React, { useState, useEffect, useRef } from 'react'
import type { GridCellProps } from '../types/financials.types'

export const GridCell: React.FC<GridCellProps> = ({
	lineItem,
	quarter,
	value,
	isCalculated,
	isEditing,
	isReadOnly = false,
	validationRule,
	onEdit,
	onChange,
	onBlur,
	onKeyDown,
}) => {
	const [inputValue, setInputValue] = useState(value.toString())
	const inputRef = useRef<HTMLInputElement>(null)

	// Focus input when entering edit mode
	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus()
			inputRef.current.select()
		}
	}, [isEditing])

	// Format currency for display
	const formatCurrency = (val: number): string => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(val)
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value
		// Allow only numbers, negative sign, and decimal point
		if (/^-?\d*\.?\d*$/.test(val) || val === '' || val === '-') {
			setInputValue(val)
		}
	}

	const handleBlur = () => {
		const numValue = parseFloat(inputValue)
		if (!isNaN(numValue)) {
			onChange(numValue)
		}
		onBlur()
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleBlur()
		} else if (e.key === 'Escape') {
			setInputValue(value.toString())
			onBlur()
		}
		onKeyDown(e)
	}

	// Determine cell styling based on state
	const getCellClasses = () => {
		const baseClasses = 'px-3 py-2 text-right font-mono text-base border-r border-b border-gray-300'
		
		if (isCalculated) {
			return `${baseClasses} bg-blue-50 text-gray-700`
		}
		
		if (isEditing) {
			return `${baseClasses} bg-white`
		}
		
		if (validationRule) {
			switch (validationRule.type) {
				case 'error':
					return `${baseClasses} bg-red-50 border-red-500 border-2`
				case 'warning':
					return `${baseClasses} bg-orange-50 border-orange-500`
				case 'info':
					return `${baseClasses} bg-blue-50`
				default:
					return `${baseClasses} bg-white hover:bg-blue-50`
			}
		}
		
		return `${baseClasses} bg-white hover:bg-blue-50 cursor-pointer`
	}

	return (
		<td
			className={getCellClasses()}
			onClick={() => !isCalculated && !isReadOnly && !isEditing && onEdit()}
			role="gridcell"
			aria-label={`${quarter.label} ${lineItem.label}: ${formatCurrency(value)}`}
			aria-readonly={isCalculated || isReadOnly}
		>
			{isEditing && !isCalculated && !isReadOnly ? (
				<div className="relative">
					<input
						ref={inputRef}
						type="text"
						value={inputValue}
						onChange={handleChange}
						onBlur={handleBlur}
						onKeyDown={handleKeyDown}
						className="w-full text-right font-mono bg-transparent border-2 border-blue-500 rounded px-2 py-1 focus:outline-none"
						aria-label={`Edit ${quarter.label} ${lineItem.label}`}
					/>
					<div className="absolute -bottom-6 right-0 text-xs text-gray-500 whitespace-nowrap">
						💾 Saving...
					</div>
				</div>
			) : (
				<div className="flex items-center justify-end">
					<span className={value < 0 ? 'text-red-600' : ''}>{formatCurrency(value)}</span>
					{validationRule && (
						<span className="ml-1" title={validationRule.message}>
							{validationRule.type === 'error' && '⚠️'}
							{validationRule.type === 'warning' && '⚠'}
							{validationRule.type === 'info' && 'ℹ️'}
						</span>
					)}
				</div>
			)}
		</td>
	)
}

